'use client';
import React, { useEffect, useRef, useState } from 'react';
import { animate } from 'motion/react';
import { metrics, testimonials, type Metric } from '@/data/movio';
import { presentation, usePresentation } from '@/lib/presentation/store';

const format = (v: number) => Math.round(v).toLocaleString('en-IN');

function MetricValue({ metric, active, delay }: { metric: Metric; active: boolean; delay: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !active) return;
    if (presentation.get().reducedMotion) {
      el.textContent = format(metric.value);
      return;
    }
    el.textContent = '0';
    const controls = animate(0, metric.value, {
      duration: 1.8,
      delay,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => (el.textContent = format(v)),
    });
    return () => controls.stop();
  }, [active, delay, metric.value]);
  // Server-rendered with the final figure, so crawlers and no-JS readers get real numbers.
  return <span ref={ref}>{format(metric.value)}</span>;
}

/**
 * Floating growth UI for the success-stories scene: verified metrics that count up while the
 * network grows in 3D, plus a rotating partner result.
 */
export function GrowthPanel({ index }: { index: number }) {
  const active = usePresentation((s) => s.index === index);
  const [story, setStory] = useState(0);

  useEffect(() => {
    if (!active || presentation.get().reducedMotion) return;
    const id = window.setInterval(() => setStory((s) => (s + 1) % testimonials.length), 6000);
    return () => window.clearInterval(id);
  }, [active]);

  const t = testimonials[story];

  return (
    <div className="v2-growth">
      <ul className="v2-metrics" aria-label="MOVIO today">
        {metrics.map((m, i) => (
          <li key={m.label} className="v2-metric">
            <span className="v2-metric-value">
              {m.prefix}
              <MetricValue metric={m} active={active} delay={0.7 + i * 0.18} />
              {m.suffix}
            </span>
            <span className="v2-metric-label">{m.label}</span>
          </li>
        ))}
      </ul>
      <figure className="v2-quote" aria-live="off">
        <p className="v2-quote-metric">{t.metric}</p>
        <blockquote className="v2-quote-text">
          {t.before} <span aria-hidden="true">→</span>
          <span className="sr-only">became</span> <strong>{t.after}</strong>
        </blockquote>
        <figcaption className="v2-quote-by">
          {t.name}, {t.role}
        </figcaption>
      </figure>
    </div>
  );
}
